import React, { useState, useEffect } from "react";
import { makeStyles } from '@mui/styles';
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

const useStyles = makeStyles(theme => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3, 4, 3),
    borderRadius: "15px",
    width: "420px",
    outline: "none",
    zIndex: 99999,
    position: "relative",
  },
  warningIcon: {
    fontSize: "48px",
    textAlign: "center",
    marginBottom: "10px"
  },
  title: {
    color: '#d32f2f',
    marginBottom: "15px",
    textAlign: 'center',
    fontWeight: 'bold'
  },
  description: {
    textAlign: 'center',
    marginBottom: "20px",
    lineHeight: 1.6,
    fontSize: "14px",
    color: "#333"
  },
  checkboxLabel: {
    fontSize: "13px",
    color: "#333",
    marginBottom: "15px"
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: "10px"
  }
}));

export default function AudioAlertModal({ onConfirm, open, audioAction, count, limit }) {
  const classes = useStyles();
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (open) {
      setConfirmed(false);
    }
  }, [open]);

  // limit = Speaker Warnings (audioproctoringcount). N warnings are allowed; the (N+1)th
  // terminates - same rule as the abort check in Test.jsx (proctoringWarningLimit.js).
  const warningLimit = Number.isFinite(Number(limit)) && limit !== null && limit !== "" ? Number(limit) : 3;
  const isTerminated = audioAction === "S" && count > warningLimit;
  const isFinalWarning = audioAction === "S" && count === warningLimit;

  return (
    <div>
      <Modal
        open={open}
        disableEnforceFocus={true}
        disableEscapeKeyDown={true}
        aria-labelledby="audio-alert-modal"
        className={classes.modal}
        style={{ zIndex: 99999 }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            style: { zIndex: 99998 },
          },
        }}
      >
        <Fade in={open}>
          <div className={classes.paper}>
            <div className={classes.warningIcon}>
              {isTerminated ? "\ud83d\udeab" : "\u26a0\ufe0f"}
            </div>

            {/* Title */}
            {isTerminated ? (
              <h3 className={classes.title}>
                Test Terminated — Rule Violation
              </h3>
            ) : isFinalWarning ? (
              <h3 className={classes.title}>
                Test Paused — Final Warning
              </h3>
            ) : audioAction === "S" && count >= 2 ? (
              <h3 className={classes.title}>
                Test Paused — Warning {count} of {warningLimit}
              </h3>
            ) : (
              <h3 className={classes.title}>
                Test Paused — Violation of Rules
              </h3>
            )}

            {/* Description */}
            {isTerminated ? (
              <p className={classes.description}>
                You have <strong>repeatedly violated</strong> the test rule of no multiple speakers.
                <br /><br />
                Your test has been <strong>terminated</strong>.
              </p>
            ) : isFinalWarning ? (
              <p className={classes.description}>
                This is your <strong>final warning</strong>. Multiple speakers have been detected {count} times.
                Your test has been <strong>paused</strong>.
                <br /><br />
                If this happens again, your test will be <strong>automatically terminated</strong>.
              </p>
            ) : audioAction === "S" && count >= 2 ? (
              <p className={classes.description}>
                Multiple speakers have been detected <strong>again</strong>.
                Your test has been <strong>paused</strong>.
                <br /><br />
                This is warning <strong>{count} of {warningLimit}</strong>. Please ensure no one else is speaking near you.
              </p>
            ) : (
              <p className={classes.description}>
                Our system has detected <strong>multiple speakers</strong> during your test.
                This is a <strong>violation of test rules</strong>. Your test has been <strong>paused</strong>.
                <br /><br />
                Please ensure you are alone and no one else is speaking near you before continuing.
              </p>
            )}

            {/* Terminated: just a close button */}
            {isTerminated ? (
              <div className={classes.buttonContainer}>
                <button
                  className="btn btn-blue"
                  style={{ borderRadius: "10px", padding: "8px 30px" }}
                  onClick={onConfirm}
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <span className={classes.checkboxLabel}>
                      I acknowledge the violation and confirm that I am taking this test alone with no other speakers nearby.
                    </span>
                  }
                  style={{ alignItems: 'flex-start', marginLeft: 0 }}
                />
                <div className={classes.buttonContainer}>
                  <button
                    className="btn btn-blue"
                    style={{
                      borderRadius: "10px",
                      opacity: confirmed ? 1 : 0.5,
                      cursor: confirmed ? "pointer" : "not-allowed",
                      padding: "8px 30px"
                    }}
                    onClick={() => confirmed && onConfirm()}
                    disabled={!confirmed}
                  >
                    Continue Test
                  </button>
                </div>
              </>
            )}
          </div>
        </Fade>
      </Modal>
    </div>
  );
}
